import { forwardRef, useEffect, useRef } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "./VideoPlayer.css";

const VideoPlayer = forwardRef(function VideoPlayer({ src, title, poster, mimeType, renditions = [], onTimeUpdate, onPause, onEnded }, forwardedRef) {
  const host = useRef(null);
  const renditionKey = renditions.filter(Number.isFinite).join(",");
  const callbacks = useRef({ onTimeUpdate, onPause, onEnded });
  callbacks.current = { onTimeUpdate, onPause, onEnded };
  useEffect(() => {
    const video = document.createElement("video");
    video.className = "video-js vjs-big-play-centered";
    video.setAttribute("playsinline", "");
    video.setAttribute("aria-label", `Video: ${title}`);
    const hls = /\.m3u8(?:[?#]|$)/i.test(src) || /mpegurl/i.test(mimeType || "");
    if (hls) video.setAttribute("crossorigin", "use-credentials");
    host.current.appendChild(video);
    const player = videojs(video, {
      controls: true, responsive: true, fluid: true, aspectRatio: "16:9",
      html5: { vhs: { withCredentials: hls, overrideNative: !videojs.browser.IS_SAFARI } },
      preload: "metadata", poster: poster || "", playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 2],
      sources: [{ src, type: mimeType || (/\.webm(?:[?#]|$)/i.test(src) ? "video/webm" : /\.m3u8(?:[?#]|$)/i.test(src) ? "application/x-mpegURL" : "video/mp4") }],
    });
    if (hls) {
      const control = player.controlBar.addChild("Component", {}, player.controlBar.children().length - 1);
      control.addClass("vjs-resolution-control");
      const select = document.createElement("select");
      select.setAttribute("aria-label", "Video resolution");
      select.title = "Video resolution";
      select.disabled = true;
      player.on("loadedmetadata", () => { select.disabled = false; });
      select.add(new Option("Auto", "auto"));
      const heights = renditionKey.split(",").map(Number).filter(value => value > 0);
      heights.sort((a, b) => b - a).forEach(height => select.add(new Option(`${height}p`, String(height))));
      control.el().appendChild(select);
      select.addEventListener("change", () => {
        const representations = player.tech()?.vhs?.representations?.();
        if (representations?.length) {
          // Enable the target first so there is always at least one usable rendition.
          representations.filter(rep => select.value === "auto" || rep.height === Number(select.value)).forEach(rep => rep.enabled(true));
          representations.filter(rep => select.value !== "auto" && rep.height !== Number(select.value)).forEach(rep => rep.enabled(false));
        } else {
          // Native Safari HLS has no VHS API; switch its playlist and restore playback position.
          callbacks.current.onPause?.({ currentTarget: video });
          select.disabled = true;
          const time = player.currentTime(), paused = player.paused(), rate = player.playbackRate();
          player.one("loadedmetadata", () => {
            player.currentTime(time); player.playbackRate(rate);
            if (!paused) player.play()?.catch(() => {});
          });
          player.src({ src: select.value === "auto" ? src : src.replace(/master\.m3u8(?=[?#]|$)/, `${select.value}p.m3u8`), type: "application/x-mpegURL" });
        }
      });
    }
    // Keep the native element available for actual watched-range tracking.
    if (typeof forwardedRef === "function") forwardedRef(video);
    else if (forwardedRef) forwardedRef.current = video;
    const listeners = Object.entries({ timeupdate: "onTimeUpdate", pause: "onPause", ended: "onEnded" })
      .map(([event, callback]) => {
        const listener = () => callbacks.current[callback]?.({ currentTarget: video });
        video.addEventListener(event, listener);
        return [event, listener];
      });
    return () => {
      callbacks.current.onPause?.({ currentTarget: video });
      listeners.forEach(([event, listener]) => video.removeEventListener(event, listener));
      if (typeof forwardedRef === "function") forwardedRef(null);
      else if (forwardedRef) forwardedRef.current = null;
      player.dispose();
    };
  }, [src, title, poster, mimeType, forwardedRef, renditionKey]);
  return <div role="region" aria-label={`Player: ${title}`} className="lesson-player min-w-0" data-vjs-player><div ref={host} /></div>;
});
export default VideoPlayer;
